import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import {ViewConfigPayload, ViewId, WORKBENCH_VIEW_CONFIG, WorkbenchViewConfig} from '../../shared/types';
import {ViewContainerId} from '../../shared/components/viewcontainers/viewcontainers';
import {DeviceId, ProjectId, UserId} from '@webmate-web-core/shared/types/ids';
import {DeviceService} from '@webmate-web-core/shared/services/device/device.service';
import {AuthService} from '@webmate-web-core/shared/services/auth.service';
import {FeatureService} from '@webmate-web-core/shared/services/feature.service';
import {UserService} from '@webmate-web-core/shared/services/user.service';
import {UserInfo} from '@webmate-web-core/shared/types/user/user-info';
import {Device} from '@webmate-web-core/shared/types/devices/device';
import {CLOSED, CONNECTED, DISCONNECTED, INITIAL} from '../../../webmate/shared/console/interfaces/console-config.interface';
import {catchError, filter, mergeMap, take} from 'rxjs/operators';
import {ConsoleSessionService} from '@webmate-web-core/shared/services/device/console-session.service';
import {ConsoleSession} from '@webmate-web-core/shared/types/console-session';
import {
    DevicePropertyName,
    DevicePropertyNames,
    getPlatformFamily,
    PlatformFamily
} from '@webmate-web-core/shared/types/devices/device-properties';
import {Subscription, throwError} from 'rxjs';
import {WorkbenchViewComponent} from '../../shared/components/viewcontainers/workbench-view.component';
import {faSyncAlt, faVolume, faVolumeMute, faVolumeOff} from '@fortawesome/pro-solid-svg-icons';
import {faPlug} from '@fortawesome/pro-light-svg-icons';
import {WorkbenchLayoutManager} from '../../shared/services/workbench-layout-manager.service';
import {WorkbenchContextService} from '../../shared/services/workbench-context.service';
import {WorkbenchKeyValStore} from '../../shared/services/workbench-store.service';
import {ConsoleService} from './services/console.service';
import {KeepAliveTimerService} from '../../shared/services/keep-alive-timer.service';
import {WorkbenchConsole} from './consoles/workbench-console';
import {WorkbenchConsoleService} from '../../shared/services/workbench-console.service';
import {WorkbenchExpeditionService} from '../../shared/services/workbench-expedition.service';
import {AudioStreamingService} from './services/audio-streaming.service';
import {ConsoleToolbarComponent} from './console-tools/console-toolbar.component';
import {WorkbenchDeviceUpdateNotificationService} from '../../shared/services/workbench-device-update-notification.service';
import {A11yToolsPlugin} from '../a11y-tools/a11y-tools-plugin';
import {
    hasActiveVisionSimulation
} from '../a11y-tools/webgl-visual-impairment.service';
import {
    ImpairedVisionSimulationConfig,
    ImpairedVisionSimulationName
} from '../a11y-tools/views/impaired-vision-simulation-control-view.component';

export interface DeviceConsoleViewConfig extends ViewConfigPayload {
    data: {
        deviceId: DeviceId,
        additionalStartupProperties?: Array<DevicePropertyName>
    }
}

@UntilDestroy()
@Component({
    templateUrl: './device-console-view.component.html',
    styleUrls: ['./device-console-view.component.less'],
    providers: [WorkbenchDeviceUpdateNotificationService]
})
export class DeviceConsoleViewComponent extends WorkbenchViewComponent implements OnInit, OnDestroy {

    @ViewChild(WorkbenchConsole) consoleElement?: WorkbenchConsole;
    @ViewChild(ConsoleToolbarComponent) toolbar?: ConsoleToolbarComponent;

    projectId: ProjectId;
    deviceId: DeviceId;
    device?: Device;
    devicePlatformFamily: PlatformFamily;
    /** Headless devices render the WebRTC viewer variant instead of an interactive remote console. */
    isHeadless = false;
    /** Guards against re-subscribing to the browser session name on every device$ emission. */
    private headlessNameSubscriptionStarted = false;
    consoleSession?: ConsoleSession;

    stateEvents = new EventEmitter<string>();

    isActiveConsole = false;
    isActiveView = false;

    offerReconnect = false;
    connecting = true;
    consoleState: string = INITIAL;

    singleUserConsoleFeatureActive = false;
    /** Only meaningful once offerReconnect is true: our own session was already terminated by then, so any
     * remaining ConsoleAccess ticket on the device necessarily belongs to someone else. */
    accessedByOtherUser = false;
    otherUserInfo?: UserInfo;

    signalingSocket?: WebSocket;

    audioStreamingAvailable = false;
    audioStreamingActive = false;
    updatingAudioStreaming = false;
    streamingPlayingAudio = false;
    audioStreamConnected = false;
    isBrowserTabVisible = !document.hidden;
    isConsoleSelectedInViewContainer = false;
    viewContainerId?: ViewContainerId;

    deviceRunning = false;
    deviceSetupDone = false;

    @ViewChild('audio') audioElement: ElementRef;

    @ViewChild('consoleContainer', { static: false })
    consoleContainer!: ElementRef<HTMLDivElement>;

    isVisionSimToolRegistered: boolean = false;
    visionSimulations?: Record<ImpairedVisionSimulationName, ImpairedVisionSimulationConfig>;
    showVisionSimOverlay: boolean = false;


    constructor(@Inject(WORKBENCH_VIEW_CONFIG) public config: WorkbenchViewConfig<DeviceConsoleViewConfig>,
                private deviceService: DeviceService,
                private store: WorkbenchKeyValStore,
                private cdr: ChangeDetectorRef,
                layoutManager: WorkbenchLayoutManager,
                private consoleSessionService: ConsoleSessionService,
                private consoleService: ConsoleService,
                private workbenchConsoleService: WorkbenchConsoleService,
                private workbenchExpeditionService: WorkbenchExpeditionService,
                private audioStreamingService: AudioStreamingService,
                private keepAliveTimerService: KeepAliveTimerService,
                private authService: AuthService,
                private featureService: FeatureService,
                private userService: UserService,
                private readonly workbenchContextService: WorkbenchContextService) {
        super(layoutManager, config);
        this.deviceId = this.config.payload!.data.deviceId;
        this.store.setValue('activeDeviceId', this.deviceId);
        this.projectId = this.workbenchContextService.getProjectId();
    }

    ngOnDestroy() {
        this.resetLocalAudioPlayback();
        if (this.audioStreamingActive) {
            this.audioStreamingService.tearDownAudioStreaming(this.deviceId).pipe(take(1)).subscribe({
                next: () => {},
                error: () => {}
            });
        }
        if (this.consoleSession) {
            this.workbenchContextService.setContextRemoveOpenedConsole(this.consoleSession.id);
            // Release the ConsoleAccess ticket immediately instead of waiting on the server's keepalive timeout sweep.
            this.consoleSessionService.terminateConsoleSession$(this.consoleSession.id).pipe(take(1)).subscribe({
                next: () => {},
                error: () => {}
            });
        }
        if (this.keepAliveSubscription) {
            this.keepAliveSubscription.unsubscribe();
        }
        if (this.deviceSubscription) {
            this.deviceSubscription.unsubscribe();
        }
    }


    ngOnInit() {
        this.featureService.getFeature('webmate.console.single-user-only').pipe(untilDestroyed(this)).subscribe(f => {
            this.singleUserConsoleFeatureActive = f?.isActive ?? false;
            this.cdr.markForCheck();
        });

        // if we become active, we make our device the active device in the workbench context
        this.layoutManager.subscribeActiveViewId$().pipe(
            untilDestroyed(this),
        ).subscribe({
            next: (activeView: ViewId) => {
                if (this.isActiveView !== (activeView === this.config.id)) {
                    this.isActiveView = activeView === this.config.id;
                    this.cdr.markForCheck();
                    if (this.isActiveView && this.consoleState === INITIAL) {
                        // Connect the console lazy, i.e. once it becomes active.
                        // This prevents canvas issues with the mobile console being loaded in the background.
                        this.connect();
                    }
                    this.maintainWorkbenchContext();
                }
            }
        });

        this.workbenchContextService.subscribeWorkbenchContext$().pipe(untilDestroyed(this)).subscribe({
            next: context => {
                // updating the context elsewhere in this component sets the active flag here
                this.isActiveConsole = context.deviceContext?.activeConsole?.consoleViewId === this.config.id;
            }
        });

        this.viewContainerId = this.layoutManager.getViewContainerIdForView(this.config.id);
        if (this.viewContainerId) {
            this.layoutManager.subscribeToNormalViewContainerChanges$(this.viewContainerId).pipe(
                untilDestroyed(this)
            ).subscribe(state => {
                this.isConsoleSelectedInViewContainer = state.viewContainerConfig.displayMode !== 'tabbed' || state.activeTab === this.config.id;
                this.updateAudioPlayback();
            });
        }

        this.isVisionSimToolRegistered = this.layoutManager.isViewRegistered(A11yToolsPlugin.ImpairedVisionSimulationViewId);
        this.store.subscribeToValue('console.impaired-vision.simulation').pipe(
            untilDestroyed(this)
        ).subscribe((simulations: Record<ImpairedVisionSimulationName, ImpairedVisionSimulationConfig> | undefined) => {
            this.visionSimulations = simulations;
            this.updateVisionSimOverlayState();
        });
    }

    /**
     * The overlay renders on top of the console canvas; headless consoles stream into a video element,
     * where the overlay would only paint a black rectangle (WMKNBN-12319). The stored simulation config
     * is kept, so the overlay reappears when a supported console becomes active again.
     */
    private updateVisionSimOverlayState() {
        this.showVisionSimOverlay = !!this.visionSimulations
            && this.isVisionSimToolRegistered
            && !this.isHeadless
            && hasActiveVisionSimulation(this.visionSimulations);
        this.cdr.markForCheck();
    }



    maintainWorkbenchContext() {
        if (this.isActiveView) {
            if (this.consoleSession && this.consoleState === CONNECTED) {
                // Use set timeout to synchronize deactivating of previous console and activating of this one
                // TODO: Find better solution for this WMKNBN-11043
                setTimeout(() => {
                    this.workbenchContextService.setContextActiveDeviceContext({
                        activeDevice: this.device.id,
                        activeConsole: {
                            consoleSession: this.consoleSession.id,
                            consoleViewId: this.config.id
                        }
                    });
                }, 10);
            } else {
                this.workbenchContextService.setContextActiveDeviceContext({
                    activeDevice: this.device?.id,
                    activeConsole: undefined
                });
            }
        }
    }

    deviceSubscription?: Subscription;

    calculateState() {
        this.deviceRunning = this.device?.state === 'running';
        const setupProperty =  this.device?.properties[DevicePropertyNames.SetupDone] !== undefined && this.device.properties[DevicePropertyNames.SetupDone] === true;
        if (this.config.payload.data.additionalStartupProperties) {
            // TODO make compatible with properties that are not strictly boolean in the future if needed
            const additionalPropertiesSetup = this.config.payload.data.additionalStartupProperties.every(p => this.device?.properties[p] === true);
            this.deviceSetupDone = setupProperty && additionalPropertiesSetup;
        } else {
            this.deviceSetupDone = setupProperty;
        }

    }

    private lastCheckedOtherUserId?: UserId;

    /**
     * Only relevant once offerReconnect is true: our own ConsoleAccess ticket was already released by then
     * (the session was terminated, or we never got one), so any ConsoleAccess ticket still on the device
     * necessarily belongs to someone else. Used to hide the pointless "Reconnect" button in single-user mode.
     */
    private updateAccessedByOtherUser(device: Device) {
        const wasAccessedByOtherUser = this.accessedByOtherUser;
        const ticket = device.tickets.find(t => t.job.name === 'ConsoleAccess');
        this.accessedByOtherUser = !!ticket;
        if (!ticket) {
            this.otherUserInfo = undefined;
            this.lastCheckedOtherUserId = undefined;
            // The device just freed up while we were waiting on the "accessed by someone else" screen:
            // reconnect automatically instead of making the user notice and click Reconnect manually.
            if (wasAccessedByOtherUser && this.offerReconnect && this.singleUserConsoleFeatureActive
                && this.isBrowserTabVisible && this.isActiveView) {
                this.reconnect();
            }
            return;
        }
        if (ticket.userId === this.lastCheckedOtherUserId) {
            return;
        }
        this.lastCheckedOtherUserId = ticket.userId;
        this.userService.getInformationAboutUser(ticket.userId).pipe(take(1)).subscribe(info => {
            this.otherUserInfo = info;
            this.cdr.markForCheck();
        });
    }

    connect() {
        this.connecting = true;
        this.offerReconnect = false;

        if (this.deviceSubscription) {
            this.deviceSubscription.unsubscribe();
        }

        const device$ = this.deviceService.getDevice$(this.deviceId);

        this.deviceSubscription = device$.subscribe({
            next: device => {
                this.device = device;
                this.isHeadless = device.isHeadlessDevice();
                this.updateVisionSimOverlayState();
                this.calculateState();
                this.syncAudioStreamingStateFromDevice();
                this.updateAccessedByOtherUser(device);
                if (this.isHeadless) {
                    // Headless device names are opaque/generated; identify the tab by its browser session instead.
                    if (!this.headlessNameSubscriptionStarted) {
                        this.headlessNameSubscriptionStarted = true;
                        this.maintainHeadlessConsoleName();
                    }
                } else {
                    this.layoutManager.changeViewName(this.config.id, this.device.name);
                }
                this.devicePlatformFamily = getPlatformFamily(device.properties);
            },
            complete: () => {
                // getDevice$ completes when the device is removed from the backend (Device.Removed) —
                // nothing else completes it (see DeviceService._handleEvents; the per-project event
                // subjects are never completed). The device is gone for good, so close the tab instead
                // of freezing on a stream that will never resume or leaving a "Reconnect" button that
                // can only fail. Also covers releases triggered elsewhere: the device page, another
                // workbench, a popped-out console, a redeploy. Idempotent with the CLOSED handler.
                this.closeConsoleView();
            }
        });

        device$.pipe(
            catchError((e) => {
                console.error('Could not get device. Closing console.', this.deviceId);
                setTimeout(() => {
                    this.layoutManager.closeView(this.config.id);
                })
                return throwError(e);
            }),
            // Headless devices only need to be running: their viewer stream does not depend on the
            // interactive-console setup (e.g. the Selenium-node startup property) that gates other consoles.
            filter(device => this.deviceRunning && (device.isHeadlessDevice() || this.deviceSetupDone)),
            take(1),
            mergeMap(device => {
                this.setupAudioStreaming();
                return this.consoleSessionService.requestConsoleSession$(device.id);
            })
        ).subscribe({
            next: (consoleSession) => {
                this.consoleSession = consoleSession;
                this.connecting = false;
                this.setupSessionEventHandling();
                this.workbenchContextService.setContextActiveConsole({
                    consoleSession: consoleSession.id,
                    consoleViewId: this.config.id
                });
                this.cdr.markForCheck();
            }, error: err => {
                console.error('DeviceConsoleViewComponent error', err);
                this.offerReconnect = true;
                this.connecting = false;
                this.cdr.markForCheck();
            }
        });
    }

    /**
     * Names the console tab after the device's active browser session instead of the device itself:
     * headless devices are ephemeral and their names are opaque/generated, whereas the browser session
     * is what the user actually recognizes. Platform is deliberately omitted, matching the browser
     * sessions view's headless handling.
     */
    private maintainHeadlessConsoleName(): void {
        this.workbenchExpeditionService.observeActiveExpeditionsInProject$({deviceId: this.deviceId}).pipe(
            untilDestroyed(this)
        ).subscribe(sessions => {
            const session = sessions[0];
            if (!session) {
                return;
            }
            // Deferred like maintainWorkbenchContext(): observeActiveExpeditionsInProject$ can replay a
            // cached value synchronously to a new subscriber, so this can run within the same change
            // detection pass that already rendered the tab bar with the old name — mutating it inline
            // would trigger ExpressionChangedAfterItHasBeenCheckedError (WMKNBN-11043-style issue).
            setTimeout(() => {
                this.layoutManager.changeViewName(this.config.id, `${session.browser.browserType} ${session.browser.version}`);
            });
        });
    }

    reconnect() {
        this.resetLocalAudioPlayback();
        this.audioStreamingService.resetAudioStream(this.deviceId);
        this.device = undefined;
        this.consoleSession = undefined;
        this.connect();
    }

    /** Local mute for the headless console's audio. Passed to the child, which flips its <video>.muted.
     *  Held here (not in the child) so it survives a reconnect, which only remounts the child. */
    headlessMuted = true;
    toggleHeadlessMute() {
        this.headlessMuted = !this.headlessMuted;
        this.cdr.markForCheck();
    }

    /**
     * Close this console tab. Idempotent so the two auto-close triggers (a server-side consoleClosed
     * signal and the backing device being removed) can race without closing twice. Deferred, matching
     * the connect() catchError→closeView pattern, to avoid closing mid-change-detection.
     * closeView() runs ngOnDestroy, which already terminates the console session and unsubscribes.
     */
    private closingView = false;
    private closeConsoleView(): void {
        if (this.closingView) {
            return;
        }
        this.closingView = true;
        setTimeout(() => this.layoutManager.closeView(this.config.id));
    }

    @HostListener('document:visibilitychange')
    onDocumentVisibilityChange() {
        this.isBrowserTabVisible = !document.hidden;
        this.updateAudioPlayback();
        // A long background period can freeze the tab (pausing even the worker keepalive) and let
        // the server tear the session down. When we return to the foreground, recover via the
        // existing reconnect path instead of waiting for a manual "Reconnect" click.
        // Skip this when the device is exclusively held by someone else: reconnecting would just fail again.
        if (this.isBrowserTabVisible && this.isActiveView && this.offerReconnect
            && !(this.singleUserConsoleFeatureActive && this.accessedByOtherUser)) {
            this.reconnect();
        }
    }

    @HostListener('window:resize')
    onLayoutVisibilityChange() {
        this.updateAudioPlayback();
    }

    // Fires on a full browser tab/window close, unlike ngOnDestroy which isn't reliably run in that case.
    // A regular HttpClient POST would get cancelled mid-flight here, so use sendBeacon instead, which the
    // browser guarantees to (attempt to) deliver even after the page is gone. sendBeacon cannot set an
    // Authorization header, so the auth token is passed as a query parameter instead (same fallback the
    // backend already supports for images/videos/websockets).
    @HostListener('window:beforeunload')
    onWindowBeforeUnload() {
        if (!this.consoleSession) {
            return;
        }
        const url = this.authService.makeAuthorizedUrl('/console/' + this.consoleSession.id + '/terminate');
        navigator.sendBeacon(url, new Blob([JSON.stringify({})], {type: 'application/json'}));
    }

    private stateEventSubscription: Subscription;
    private keepAliveSubscription: Subscription;
    private consoleEventSubscription: Subscription;
    private audioPlaybackSubscription?: Subscription;
    setupSessionEventHandling() {
        if (this.keepAliveSubscription) {
            this.keepAliveSubscription.unsubscribe();
        }
        // Start sending keepalive messages. The cadence is driven by a Web Worker rather than a
        // main-thread RxJS interval so that background-tab timer throttling cannot stall the
        // keepalive and let the server time the session out.
        let keepAliveTime = this.consoleSession.keepAliveTime ?? 10000 /* 10 seconds default */;
        this.keepAliveSubscription = this.keepAliveTimerService.ticks(keepAliveTime).pipe(mergeMap(() => {
                if (!this.consoleSession) {
                    this.keepAliveSubscription.unsubscribe();
                    this.keepAliveSubscription = undefined;
                } else {
                    return this.consoleService.sendKeepAlive(this.consoleSession.id);
                }
        })).subscribe();

        if (this.stateEventSubscription) {
            this.stateEventSubscription.unsubscribe();
        }
        this.stateEventSubscription = this.stateEvents.pipe(untilDestroyed(this)).subscribe(state => {
            this.consoleState = state;
            if (state === DISCONNECTED) {
                this.resetLocalAudioPlayback();
                this.audioStreamingService.resetAudioStream(this.deviceId);
                this.consoleSession = undefined;
                this.device = undefined;
                this.offerReconnect = true;
                this.maintainWorkbenchContext();
                this.cdr.markForCheck();
            } else if (state === CONNECTED) {
                this.maintainWorkbenchContext();
                this.cdr.markForCheck();
            } else if (state === CLOSED) {
                // The session/console was closed server-side (e.g. the browser session was stopped).
                // Close the tab instead of offering a pointless reconnect, matching how consoles
                // disappear when their device is deleted.
                this.closeConsoleView();
            }
        });

        if (this.consoleEventSubscription) {
            this.consoleEventSubscription.unsubscribe();
        }
        this.consoleEventSubscription = this.workbenchConsoleService.getWorkbenchConsoleEvent$(this.device.id).pipe(untilDestroyed(this)).subscribe(e => {
            if (this.isActiveView) {
                if (e.type.startsWith('toolbar:')) {
                    this.toolbar?.handleConsoleEvent(e);
                } else {
                    this.consoleElement?.handleConsoleEvent(e);
                }
            }
        })
    }

    setupAudioStreaming() {
        if (this.audioStreamingAvailable && !this.audioStreamingActive && !this.updatingAudioStreaming) {
            this.updatingAudioStreaming = true;
            this.audioStreamingService.setupAudioStreaming(this.deviceId).subscribe({
                next: () => {
                    // Each streaming generation has a new producer on the mediaserver,
                    // so any stream/transport from a previous generation is dead.
                    // Drop it so playback builds a fresh WebRTC consumer.
                    this.resetLocalAudioPlayback();
                    this.audioStreamingService.resetAudioStream(this.deviceId);
                    this.updatingAudioStreaming = false;
                    this.audioStreamingActive = true;
                    this.updateAudioPlayback();
                },
                error: (_e) => {
                    this.updatingAudioStreaming = false;
                },
            });
        }
    }

    startPlayingAudio() {
        if (this.streamingPlayingAudio) {
            return;
        }
        if (!this.audioElement?.nativeElement) {
            setTimeout(() => this.updateAudioPlayback());
            return;
        }
        if (this.audioStreamConnected && this.audioElement.nativeElement.srcObject) {
            void this.audioElement.nativeElement.play().then(() => {
                this.streamingPlayingAudio = true;
            }).catch(() => {
                this.streamingPlayingAudio = false;
            });
            return;
        }
        if (this.audioPlaybackSubscription) {
            return;
        }
        this.audioPlaybackSubscription = this.audioStreamingService.startAudioStream(this.deviceId).subscribe(m => {
            this.audioElement.nativeElement.srcObject = m;
            this.audioStreamConnected = true;
            void this.audioElement.nativeElement.play().then(() => {
                this.streamingPlayingAudio = true;
            }).catch(() => {
                this.streamingPlayingAudio = false;
            });
        });

    }

    stopPlayingAudio() {
        this.resetLocalAudioPlayback();
        this.tearDownAudioStreaming();
    }

    tearDownAudioStreaming () {
        this.updatingAudioStreaming = true;
        this.audioStreamingService.tearDownAudioStreaming(this.deviceId).subscribe({
            next: () => {
                this.finishTearDownAudioStreaming();
            },
            error: () => {
                this.finishTearDownAudioStreaming();
            }
        });
    }

    private finishTearDownAudioStreaming() {
        // Clean up again at teardown completion: device updates arriving while
        // the teardown was in flight may have re-created playback resources.
        this.resetLocalAudioPlayback();
        this.audioStreamingService.resetAudioStream(this.deviceId);
        this.updatingAudioStreaming = false;
        this.audioStreamingActive = false;
        this.updateAudioPlayback();
    }

    private syncAudioStreamingStateFromDevice() {
        this.audioStreamingAvailable = this.device?.properties[DevicePropertyNames.AudioStreamingAvailable] === true;
        this.audioStreamingActive = this.device?.properties[DevicePropertyNames.AudioStreamingInfo]?.streamingActive === true;
        this.updateAudioPlayback();
    }

    private shouldPlayAudio() {
        const isVisibleInLayout = this.viewContainerId
            ? this.layoutManager.isViewContainerShown(this.viewContainerId)
            : this.isActiveView;
        // While a start/stop transition is in flight, device property updates still
        // report the old streamingActive value. Starting playback in that window
        // would attach to a stream that is about to die, so hold off until the
        // transition settles.
        return this.audioStreamingAvailable && this.audioStreamingActive && !this.updatingAudioStreaming
            && isVisibleInLayout && this.isConsoleSelectedInViewContainer && this.isBrowserTabVisible;
    }

    private updateAudioPlayback() {
        if (this.shouldPlayAudio()) {
            this.startPlayingAudio();
        } else {
            // Visibility changes only pause local playback.
            // The local stream stays attached until an explicit stop, disconnect,
            // reconnect, or console destruction resets it.
            this.pauseAudioPlayback();
        }
    }

    private pauseAudioPlayback() {
        if (this.audioElement?.nativeElement) {
            this.audioElement.nativeElement.pause();
        }
        this.streamingPlayingAudio = false;
    }

    private resetLocalAudioPlayback() {
        this.pauseAudioPlayback();
        if (this.audioPlaybackSubscription) {
            this.audioPlaybackSubscription.unsubscribe();
            this.audioPlaybackSubscription = undefined;
        }
        if (this.audioElement?.nativeElement) {
            this.audioElement.nativeElement.srcObject = null;
        }
        this.audioStreamConnected = false;
    }


    // startAudioStreaming() {
    //     this.updatingStreaming = true;
    //
    //     this.deviceService.imposeDevicePropertyRequirement(this.deviceId, 'streaming.audio.requested', true).pipe(
    //         switchMap(_ => this.deviceService.getDevice$(this.deviceId).pipe(
    //             filter(device => {
    //                 const result = device.properties['streaming.audio.info']?.streamingActive === true;
    //                 return result;
    //             }),
    //             // takeUntilDestroyed(this),
    //             timeout(20000),
    //             catchError(err => throwError(() => 'Start audio streaming has timed out'))
    //         ))
    //     ).subscribe({
    //         next: () => {
    //             this.updatingStreaming = false;
    //             this.streamingActive = true;
    //             this.startPlayingAudio();
    //             this.cdr.detectChanges();
    //         },
    //         error: err => {
    //             console.error('DeviceConsoleViewComponent startAudioStreaming error', err);
    //             this.updatingStreaming = false;
    //             this.cdr.detectChanges();
    //         }
    //     });
    // }

    // stopPlayingAudio() {
    //     this.updatingStreaming = true;
    //     this.deviceService.imposeDevicePropertyRequirement(this.deviceId, 'streaming.audio.requested', false).pipe(
    //         switchMap(_ => this.deviceService.getDevice$(this.deviceId).pipe(
    //             filter(device => {
    //                 const streamingInactive = !device.properties['streaming.audio.info']?.streamingActive;
    //                 return streamingInactive;
    //             }),
    //             take(1),
    //             timeout(20_000),
    //             catchError(err => throwError(() => 'Stop audio streaming has timed out'))
    //         ))
    //     ).subscribe({
    //         next: () => {
    //             this.updatingStreaming = false;
    //             this.streamingActive = false;
    //             this.cdr.detectChanges();
    //         },
    //         error: err => {
    //             console.error("DeviceConsoleViewComponent stopAudioStreaming error", err);
    //             this.updatingStreaming = false;
    //             this.cdr.detectChanges();
    //         }
    //     });
    // }

    // stopPlayingAudio() {
    //     this.audioElement.nativeElement.pause();
    //     this.streamingPlayingAudio = false;
    // }
    //
    // pingAudioSignalingSocket() {
    //     if (!this.streamingPlayingAudio || !this.streamingActive || !this.signalingSocket) {
    //         return;
    //     }
    //
    //     // just keep the connection alive
    //     this.signalingSocket.send(JSON.stringify({type: 'ping'}));
    // }
    //
    // startPlayingAudio() {
    //     if (!this.audioStreamingData.consumer) {
    //         this.initWebRTC();
    //     } else {
    //         this.audioElement.nativeElement.play();
    //         this.streamingPlayingAudio = true;
    //     }
    // }
    //
    // initWebRTC() {
    //     const device = new MDevice();
    //
    //     const signalingSocket = new WebSocket(this.auth.makeAuthorizedWebSocketUrl(this.deviceService.getDeviceAudioWebRtcSignalingURL(this.deviceId)));
    //     this.signalingSocket = signalingSocket;
    //
    //     // const signalingSocket = new WebSocket(`ws://media1.webmate.io:3001/device-streaming/${this.deviceId}/audio/stream`);
    //
    //     const signalingEvents$ = new Subject<{
    //         type: 'open' | 'gotRouterRtpCapabilities' | 'consumerTransportCreated' | 'gotConsumeResponse' | 'preparationDone',
    //         data?: any
    //     }>();
    //
    //     signalingSocket.onmessage = (msg: MessageEvent) => {
    //         const data = JSON.parse(msg.data);
    //         if (data.type === 'getRouterRtpCapabilitiesResult') {
    //             signalingEvents$.next({type: 'gotRouterRtpCapabilities', data: data.data});
    //         } else if (data.type === 'createConsumerTransportResult') {
    //             signalingEvents$.next({type: 'consumerTransportCreated', data: data.data});
    //         } else if (data.type === 'consumeResult') {
    //             signalingEvents$.next({type: 'gotConsumeResponse', data: data.data});
    //         } else if (data.type === 'consumerTransportConnected') {
    //             this.audioStreamingData.connectCallback();
    //         } else {
    //             console.error('Unknown message type', data.type);
    //             signalingEvents$.error('Unknown message type');
    //         }
    //     };
    //
    //     signalingSocket.onerror = (err) => {
    //         console.error('signalingSocket error', err);
    //         signalingEvents$.error(err);
    //         this.signalingSocket = undefined;
    //     };
    //
    //     signalingSocket.onclose = () => {
    //         this.signalingSocket = undefined;
    //     };
    //     signalingSocket.onopen = () => {
    //         signalingEvents$.next({type: 'open'});
    //     };
    //
    //     signalingEvents$.subscribe((event) => {
    //         if (event.type === 'open') {
    //             signalingSocket.send(JSON.stringify({type: 'getRouterRtpCapabilities'}));
    //         } else if (event.type === 'gotRouterRtpCapabilities') {
    //             const routerRtpCapabilities = event.data;
    //             this.audioStreamingData.routerRtpCapabilities = routerRtpCapabilities;
    //             from(device.load({routerRtpCapabilities})).pipe(take(1), map(() => ({routerRtpCapabilities}))).subscribe({
    //                 // I know that this is nested subscription, but the alternative would be ugly at this place
    //                 next: ({routerRtpCapabilities}) => {
    //                     signalingSocket.send(JSON.stringify({type: 'createConsumerTransport'}));
    //                 },
    //                 error: err => {
    //                     console.error('error', err);
    //                 },
    //             });
    //         } else if (event.type === 'consumerTransportCreated') {
    //             const consumerTransportOptions = event.data;
    //             const transport = device.createRecvTransport(consumerTransportOptions);
    //             this.audioStreamingData.transport = transport;
    //             transport.on('connect', ({dtlsParameters}, callback, errback) => {
    //                 this.audioStreamingData.connectCallback = callback;
    //                 signalingSocket.send(JSON.stringify({
    //                     'type': 'connectConsumerTransport',
    //                     'data': {
    //                         transportId: transport.id,
    //                         dtlsParameters
    //                     }
    //                 }));
    //             });
    //
    //             transport.on('connectionstatechange', async (state) => {
    //                 switch (state) {
    //                     case 'connecting':
    //                         break;
    //
    //                     case 'connected':
    //                         await signalingSocket.send(JSON.stringify({type: 'resume'}));
    //                         break;
    //
    //                     case 'failed':
    //                         transport.close();
    //                         break;
    //
    //                     default:
    //                         break;
    //                 }
    //             });
    //             signalingSocket.send(JSON.stringify({type: 'consume', data: device.rtpCapabilities}));
    //         } else if (event.type === 'gotConsumeResponse') {
    //             const {transportId, id, producerId, kind, rtpParameters} = event.data;
    //             if (!this.audioStreamingData.transport) {
    //                 console.error('No transport');
    //                 return;
    //             }
    //
    //             const codecOptions = {};
    //             from(this.audioStreamingData.transport.consume({producerId, id, kind, rtpParameters, appData: codecOptions})).subscribe({
    //                 next: (consumer) => {
    //                     this.audioStreamingData.consumer = consumer;
    //                     signalingEvents$.next({type: 'preparationDone'});
    //                 },
    //                 error: err => {
    //                     console.error('error', err);
    //                 }
    //             });
    //         } else if (event.type === 'preparationDone') {
    //             if (!this.audioStreamingData.consumer) {
    //                 console.error('No consumer available');
    //                 return;
    //             }
    //             const stream = new MediaStream();
    //             stream.addTrack(this.audioStreamingData.consumer.track);
    //             this.audioElement.nativeElement.srcObject = stream;
    //             this.startPlayingAudio();
    //         }
    //     });
    // }

    protected readonly getPlatformFamily = getPlatformFamily;
    protected readonly faVolumeMute = faVolumeMute;
    protected readonly faVolumeOff = faVolumeOff;
    protected readonly faVolume = faVolume;
    protected readonly faPlug = faPlug;
    protected readonly faSyncAlt = faSyncAlt;
    protected readonly CONNECTED = CONNECTED;
}
